export const PRODUCT_ACTIVE_REQUIRES_ACTIVE_VARIANT_MESSAGE =
  "At least one color variant must be active before the product can be set to Active status.";

export const ADD_TO_CATALOG_REQUIRES_ACTIVE_PRODUCT_MESSAGE =
  "The source product must be active to add the product to the catalog";

export const PRODUCT_ACTIVE_REQUIRES_ACTIVE_VENDOR_MESSAGE =
  "Cannot have an active product when the associated vendor is not active";

export const DEFAULT_DECORATION_SUPPLIER_VENDOR_CHANGE_MESSAGE =
  "The previous Vendor is listed as the Default Decorations Supplier. Please make sure to update the Default Decorations Supplier as needed";

/** True when replacing the product vendor that is also the Default Decorations Supplier. */
export const shouldWarnDefaultDecorationSupplierChange = (
  currentVendorId?: string | null,
  defaultSupplierId?: string | null,
  nextVendorId?: string | null,
): boolean => {
  if (!currentVendorId || !defaultSupplierId || !nextVendorId) {
    return false;
  }

  const currentId = String(currentVendorId);
  const supplierId = String(defaultSupplierId);
  const nextId = String(nextVendorId);

  return currentId === supplierId && nextId !== supplierId;
};

export const PRODUCT_ACTIVE_REQUIRES_PRICING_MESSAGE =
  "At least one active variant must have pricing configured.";

export const PRODUCT_ACTIVE_REQUIRES_CATEGORY_MESSAGE =
  "At least one category is required.";

export type ColorVariantLike = {
  status?: string;
  enabled?: boolean;
  isDeleted?: boolean;
  pricingOption?: Array<{
    pricingMatrix?: Array<{
      price?: Array<{ value?: number | string | null }>;
    }>;
  }>;
};

export type ProductCategoryLike = {
  name?: string;
};

export type ProductActiveValidationInput = {
  variants?: ColorVariantLike[] | null;
  categories?: ProductCategoryLike[] | null;
};

export type ProductActiveValidationResult = {
  allowed: boolean;
  messages: string[];
};

export const isVariantStatusActive = (status?: string): boolean =>
  status?.toLowerCase() === "active";

export const isColorVariantActive = (variant: ColorVariantLike): boolean => {
  if (variant.isDeleted) {
    return false;
  }

  if (isVariantStatusActive(variant.status)) {
    return true;
  }

  if (typeof variant.enabled === "boolean") {
    return variant.enabled;
  }

  return false;
};

export const getNonDeletedColorVariants = (
  variants?: ColorVariantLike[] | null,
): ColorVariantLike[] =>
  (variants ?? []).filter((variant) => variant.isDeleted !== true);

export const hasActiveColorVariant = (
  variants?: ColorVariantLike[] | null,
): boolean => getNonDeletedColorVariants(variants).some(isColorVariantActive);

export const hasVariantPricingConfigured = (
  variant: ColorVariantLike,
): boolean =>
  (variant.pricingOption ?? []).some((option) =>
    (option.pricingMatrix ?? []).some((matrix) =>
      (matrix.price ?? []).some(
        (price) =>
          price?.value !== undefined &&
          price?.value !== null &&
          price?.value !== "",
      ),
    ),
  );

export const hasProductCategory = (
  categories?: ProductCategoryLike[] | null,
): boolean =>
  (categories ?? []).some((category) => Boolean(category?.name?.trim()));

export const validateProductCanBeActive = (
  product: ProductActiveValidationInput,
): ProductActiveValidationResult => {
  const messages: string[] = [];
  const colorVariants = getNonDeletedColorVariants(product.variants);

  if (colorVariants.length > 0) {
    const activeVariants = colorVariants.filter(isColorVariantActive);

    if (activeVariants.length === 0) {
      messages.push(PRODUCT_ACTIVE_REQUIRES_ACTIVE_VARIANT_MESSAGE);
    } else if (!activeVariants.some(hasVariantPricingConfigured)) {
      messages.push(PRODUCT_ACTIVE_REQUIRES_PRICING_MESSAGE);
    }
  }

  if (!hasProductCategory(product.categories)) {
    messages.push(PRODUCT_ACTIVE_REQUIRES_CATEGORY_MESSAGE);
  }

  return {
    allowed: messages.length === 0,
    messages,
  };
};

export const isProductStatusActive = (status?: string): boolean =>
  status?.toLowerCase().replace(/[-\s]/g, "_") === "active";

export const isVendorStatusActive = (status?: string): boolean =>
  status?.toLowerCase().replace(/[-\s]/g, "_") === "active";

export const hasInactiveProductsInSelection = (
  selectedIds: string[],
  products: { id: string; status?: string }[],
): boolean =>
  selectedIds.some((id) => {
    const product = products.find((p) => p.id === id);
    return Boolean(product && !isProductStatusActive(product.status));
  });

export const canSetProductToActiveStatus = (
  variants?: ColorVariantLike[] | null,
): { allowed: boolean; message?: string } => {
  const colorVariants = getNonDeletedColorVariants(variants);

  if (colorVariants.length === 0) {
    return { allowed: true };
  }

  if (hasActiveColorVariant(colorVariants)) {
    return { allowed: true };
  }

  return {
    allowed: false,
    message: PRODUCT_ACTIVE_REQUIRES_ACTIVE_VARIANT_MESSAGE,
  };
};

export const wouldLeaveProductWithoutActiveVariants = (
  variants: ColorVariantLike[],
  variantIndex: number,
  productStatus?: string,
): boolean => {
  if (!isProductStatusActive(productStatus)) {
    return false;
  }

  const hasOtherActiveVariant = variants.some(
    (variant, index) =>
      index !== variantIndex &&
      !variant.isDeleted &&
      isColorVariantActive(variant),
  );

  return !hasOtherActiveVariant;
};
